import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

const AdminPanel = () => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentResume, setCurrentResume] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const storage = getStorage();
  const db = getFirestore();

  useEffect(() => {
    // Fetch current resume info
    const fetchCurrentResume = async () => {
      const resumeDoc = await getDoc(doc(db, 'config', 'resume'));
      if (resumeDoc.exists()) {
        setCurrentResume(resumeDoc.data());
      }
    };
    fetchCurrentResume();
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Generate a unique filename with timestamp
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `resumes/${fileName}`);
      
      // Upload the file
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Store the reference in Firestore
      await setDoc(doc(db, 'config', 'resume'), {
        fileName,
        uploadDate: new Date().toISOString(),
        downloadURL,
        originalName: file.name
      });

      setCurrentResume({
        fileName,
        uploadDate: new Date().toISOString(),
        downloadURL,
        originalName: file.name
      });
      
      setSuccess('Resume uploaded successfully!');
    } catch (error) {
      setError('Failed to upload resume. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    auth.signOut();
    navigate('/admin');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Admin Panel
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {currentResume && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Current Resume
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary={currentResume.originalName}
                    secondary={`Uploaded: ${new Date(currentResume.uploadDate).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="download"
                      onClick={() => window.open(currentResume.downloadURL, '_blank')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Box>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <input
              accept="application/pdf"
              style={{ display: 'none' }}
              id="resume-file"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="resume-file">
              <Button
                variant="contained"
                component="span"
                disabled={uploading}
                sx={{ mb: 2 }}
              >
                {uploading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Upload New Resume'
                )}
              </Button>
            </label>
            
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleLogout}
              fullWidth
              sx={{ mt: 2 }}
            >
              Logout
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminPanel;